import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { SubSink } from 'subsink';
import { catchError, finalize, first, tap, map } from 'rxjs/operators';

import { DriverQualificationService } from '../service/driver-qualification.service';
import { Router } from '@angular/router';
import { ROUTE_DQF, ROUTE_DQF_DOCUMENT_UPLOAD, ROUTE_DQF_REVIEW, ROUTE_CARRIER_COMPANY } from 'src/app/shared/routes';
import { ROUTE_CARRIER_COMPANY_MENU } from 'src/app/carrier-company/shared/routes';
import { RegistrationService } from 'src/app/registration/shared/registration.service';
import { Driver } from 'src/app/shared/models/driver.model';
import { MatBottomSheet, MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { CompleteProfileSheetComponent } from '../../driver-qualification-files/complete-profile-sheet/complete-profile-sheet.component';
import { DriverApiService } from 'src/app/shared/services/driver-api.service';
import { ErrorModel } from 'src/app/shared/models/error.model';
import { DateTimeService } from 'src/app/shared/date-time-convertor/date-time.service';
import { forkJoin, of } from 'rxjs';
import { SkeletonDqfListComponent } from './skeleton-dqf-list/skeleton-dqf-list.component';
import { MatIconModule } from '@angular/material/icon';
import { environment } from 'src/environments/environment';
import { CarrierDataService } from 'src/app/shared/services/carrier-data.service';
import { PublicCarrier } from 'src/app/shared/models/public-carrier';
import {
  CompanyContact,
  formatCompanyContactTypeLabel,
  getDriverVisibleCompanyContacts,
  whatsappNumberForUrl
} from 'src/app/shared/models/company-contact';

const DQF_STATUS_CREATED = 'Created';
const DQF_STATUS_PROCESSING = 'Processing';

const PROFILE_STATUS_INCOMPLETE = 'Incomplete';

const DQF_STATUS_DRIVER_SIGNATURE_SUBMITED = 'DriverSignatureSubmitted';
const DQF_STATUS_PENDINGAPPROVAL = 'PendingCarrierApproval';
const DQF_STATUS_REJECTED = 'Rejected';
const DQF_STATUS_APPROVED = 'Approved';
const DQF_STATUS_CARRIERPROCESSINGCOMPLETED = 'CarrierProcessingCompleted';
const DQF_STATUS_APPROVEDPROCESSING = 'ApprovedProcessing';
const STATUS_CODE = 200;
const GET_DQFS = 'get all dqfs';
const GET_CARRIER_LOGO = 'get carrier company logo';
const DEAFULT_COMPANY_LOGO = '/assets/images/default-profile.png';
const DISABLE_TOP_LOADING_BAR = true;
const DQF_STATUS_WITHDRAWN = 'Withdrawn';

// Hiring Decision Status
const HIRING_DECISION_PENDING = 'pending';
const HIRING_DECISION_HIRED = 'hired';
const HIRING_DECISION_NOT_HIRED = 'notHired';

// Carrier Association Card Interface
interface CarrierAssociationCard {
  carrierCompanyId: string;
  carrierCompanyName: string;
  carrierCompanyLogo: string;
  hiredStatus: string;
  driverType: string;
  hasDqf: boolean;
  dqfStatus?: string;
  dqfId?: string;
  createdAt?: Date;
  modifiedDate?: Date;
  driverVisibleContacts?: CompanyContact[];
  contactsExpanded?: boolean;
}

@Component({
    selector: 'app-dqf-list',
    templateUrl: './dqf-list.component.html',
    styleUrls: ['./dqf-list.component.scss'],
    standalone: true,
    imports: [CommonModule, MatBottomSheetModule, SkeletonDqfListComponent, MatIconModule]
})
export class DqfListComponent implements OnInit, OnDestroy {
  dqfs: any[] = [];
  carrierAssociationCards: CarrierAssociationCard[] = [];
  private subs = new SubSink();
  driver: Driver;
  isContentReady: boolean = false;
  private readonly environment = environment;

  constructor(
    private readonly location: Location,
    private readonly router: Router,
    private readonly bottomSheet: MatBottomSheet,
    private readonly driverQualificationService: DriverQualificationService,
    private readonly registrationService: RegistrationService,
    private readonly driverApiService: DriverApiService,
    private readonly timeService: DateTimeService,
    private readonly carrierDataService: CarrierDataService
  ) {}

  ngOnInit(): void {
    this.subs.sink = this.registrationService.loadDriver(DISABLE_TOP_LOADING_BAR).subscribe((response: Driver) => {
      if (response) {
        this.driver = response;
      }
    });
    this.loadCarrierAssociations();
  }

  loadCarrierAssociations(): void {
    this.isContentReady = false;
    
    const driver = this.registrationService.getDriver();
    const companyAssociations = driver?.companyAssociations;
    
    if (!companyAssociations || companyAssociations.length === 0) {
      this.carrierAssociationCards = [];
      this.isContentReady = true;
      return;
    }

    // Fetch DQFs
    this.subs.sink = this.driverQualificationService
      .getDqfs()
      .pipe(
        first(),
        tap(dqfs => {
          const allDqfs = dqfs && dqfs.length > 0 ? this.registrationService.getDqfsWithDriverType(dqfs) : [];
          this.dqfs = allDqfs; // Keep for backward compatibility
          this.buildCarrierAssociationCards(companyAssociations, allDqfs);
        }),
        catchError((error: any) => {
          // If DQF fetch fails, still show associations without DQF data
          this.buildCarrierAssociationCards(companyAssociations, []);
          return of(false);
        })
      )
      .subscribe();
  }

    /**
   * When multiple DQFs exist for the same carrier, pick the one that should drive the card
   * (e.g. a new Created DQF over an older Approved one).
   */
    private pickRepresentativeDqfForCarrier(carrierDqfs: any[]): any | undefined {
      if (!carrierDqfs?.length) {
        return undefined;
      }
      const newestFirst = (a: any, b: any) => {
        const ta = new Date(a.modifiedDate || a.createdAt || 0).getTime();
        const tb = new Date(b.modifiedDate || b.createdAt || 0).getTime();
        return tb - ta;
      };
      const takeNewest = (list: any[]) => [...list].sort(newestFirst)[0];
  
      const incomplete = carrierDqfs.filter(
        d => d.status === DQF_STATUS_CREATED || d.status === DQF_STATUS_PROCESSING
      );
      if (incomplete.length) {
        return takeNewest(incomplete);
      }
      const pending = carrierDqfs.filter(
        d =>
          d.status === DQF_STATUS_PENDINGAPPROVAL || d.status === DQF_STATUS_DRIVER_SIGNATURE_SUBMITED
      );
      if (pending.length) {
        return takeNewest(pending);
      }
      const rejected = carrierDqfs.filter(d => d.status === DQF_STATUS_REJECTED);
      if (rejected.length) {
        return takeNewest(rejected);
      }
      const approved = carrierDqfs.filter(d =>
        [DQF_STATUS_APPROVED, DQF_STATUS_CARRIERPROCESSINGCOMPLETED, DQF_STATUS_APPROVEDPROCESSING].includes(
          d.status
        )
      );
      if (approved.length) {
        return takeNewest(approved);
      }
      const withdrawn = carrierDqfs.filter(d => d.status === DQF_STATUS_WITHDRAWN);
      if (withdrawn.length) {
        return takeNewest(withdrawn);
      }
      return takeNewest(carrierDqfs);
    }

  private buildCarrierAssociationCards(companyAssociations: any[], dqfs: any[]): void {
    // One carrier may have multiple DQFs; pick the DQF that should drive UI (e.g. Created over Approved)
    const dqfsByCarrier = new Map<string, any[]>();
    dqfs.forEach(dqf => {
      const carrierId = dqf.carrierCompanyId;
      if (!dqfsByCarrier.has(carrierId)) {
        dqfsByCarrier.set(carrierId, []);
      }
      dqfsByCarrier.get(carrierId)!.push(dqf);
    });
    const dqfMap = new Map<string, any>();
    dqfsByCarrier.forEach((list, carrierId) => {
      dqfMap.set(carrierId, this.pickRepresentativeDqfForCarrier(list));
    });

    // Build carrier association cards from all associations
    this.carrierAssociationCards = companyAssociations.map(association => {
      const dqf = dqfMap.get(association.id);
      
      return {
        carrierCompanyId: association.id,
        carrierCompanyName: dqf?.carrierCompanyName || '', // Will be fetched later
        carrierCompanyLogo: DEAFULT_COMPANY_LOGO,
        hiredStatus: association.hiredStatus || HIRING_DECISION_PENDING,
        driverType: association.driverType || dqf?.driverType || '',
        hasDqf: !!dqf,
        dqfStatus: dqf?.status,
        dqfId: dqf?.id,
        createdAt: dqf?.createdAt ? new Date(dqf.createdAt) : new Date(),
        modifiedDate: dqf?.modifiedDate ? new Date(dqf.modifiedDate) : new Date()
      };
    })
    // Filter out pending cards (not hired + no DQF)
    .filter(card => {
      // Keep the card if driver is hired OR card has a DQF
      return card.hiredStatus === HIRING_DECISION_HIRED || card.hasDqf;
    });

    // Sort carrier association cards by priority
    this.carrierAssociationCards.sort((a, b) => {
      // Priority order: Hired (Active) → Incomplete DQF → Pending DQF → Approved not hired → Rejected → Others
      const getPriority = (card: CarrierAssociationCard) => {
        // Highest priority: Hired drivers (Active)
        if (card.hiredStatus === HIRING_DECISION_HIRED) {
          return 1;
        }
        
        // Second priority: Incomplete/Created DQF (Submit DQF)
        if (card.hasDqf && (card.dqfStatus === DQF_STATUS_CREATED || card.dqfStatus === DQF_STATUS_PROCESSING)) {
          return 2;
        }
        
        // Third priority: Pending DQF (Under Review)
        if (card.hasDqf && card.dqfStatus === DQF_STATUS_PENDINGAPPROVAL) {
          return 3;
        }
        
        // Fourth priority: Approved DQF but not hired
        if (card.hasDqf && this.isApproved(card.dqfStatus)) {
          return 4;
        }
        
        // Fifth priority: Rejected DQF
        if (card.hasDqf && card.dqfStatus === DQF_STATUS_REJECTED) {
          return 5;
        }
        
        // Sixth priority: Withdrawn
        if (card.hasDqf && card.dqfStatus === DQF_STATUS_WITHDRAWN) {
          return 6;
        }
        
        // Seventh priority: Association without DQF (pending)
        if (!card.hasDqf && card.hiredStatus === HIRING_DECISION_PENDING) {
          return 7;
        }
        
        // Lowest priority: Not hired
        return 8;
      };
      
      const priorityA = getPriority(a);
      const priorityB = getPriority(b);
      
      // Sort by priority first
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      // If same priority, sort by date (newest first)
      return b.modifiedDate.getTime() - a.modifiedDate.getTime();
    });

    // Fetch carrier company details (names and logos)
    this.fetchCarrierCompanyDetails();
  }

  private fetchCarrierCompanyDetails(): void {
    if (this.carrierAssociationCards.length === 0) {
      this.isContentReady = true;
      return;
    }

    // Create array of carrier detail fetch calls
    const carrierDetailCalls = this.carrierAssociationCards.map(card => 
      this.carrierDataService.getCarrierPublicById(card.carrierCompanyId).pipe(
        map((carrier: PublicCarrier) => ({ 
          id: card.carrierCompanyId, 
          name: carrier.companyName,
          logo: null 
        })),
        catchError(() => of({ 
          id: card.carrierCompanyId, 
          name: 'Unknown Carrier',
          logo: null 
        }))
      )
    );

    forkJoin(carrierDetailCalls)
      .pipe(
        first(),
        tap(carrierDetails => {
          // Update carrier names
          this.carrierAssociationCards.forEach(card => {
            const details = carrierDetails.find((d: any) => d.id === card.carrierCompanyId);
            if (details && details.name) {
              card.carrierCompanyName = details.name;
            }
          });
        })
      )
      .subscribe(() => {
        // Now fetch logos
        this.fetchCarrierCompanyLogos();
      });
  }

  private fetchCarrierCompanyLogos(): void {
    const logoFetchCalls = this.carrierAssociationCards.map(card =>
      this.driverQualificationService.getCarrierCompanyLogo(card.carrierCompanyId).pipe(
        catchError(() =>
          of({ id: card.carrierCompanyId, companyLogo: null, companyContacts: [] as unknown[] })
        )
      )
    );

    forkJoin(logoFetchCalls)
      .pipe(
        first(),
        tap(responses => {
          // Assign logos to carrier association cards
          this.carrierAssociationCards.forEach(card => {
            const logoResponse = responses.find((res: any) => res.id === card.carrierCompanyId);

            if (logoResponse && logoResponse.companyLogo) {
              card.carrierCompanyLogo = logoResponse.companyLogo;
            } else {
              card.carrierCompanyLogo = DEAFULT_COMPANY_LOGO;
            }
            card.driverVisibleContacts = getDriverVisibleCompanyContacts(logoResponse?.companyContacts);
            card.contactsExpanded = false;
          });
        }),
        catchError((error: any) => {
          // Set default logo for all cards on error
          this.carrierAssociationCards.forEach(card => {
            card.carrierCompanyLogo = DEAFULT_COMPANY_LOGO;
            card.driverVisibleContacts = [];
            card.contactsExpanded = false;
          });
          return of(false);
        }),
        finalize(() => {
          this.isContentReady = true;
        })
      )
      .subscribe();
  }

  readonly contactTypeLabel = formatCompanyContactTypeLabel;

  whatsappHref(whatsApp: string | undefined): string {
    const digits = whatsappNumberForUrl(whatsApp);
    return digits ? `https://wa.me/${digits}` : '';
  }

  toggleCardContactsExpanded(event: Event, card: CarrierAssociationCard): void {
    event.stopPropagation();
    card.contactsExpanded = !card.contactsExpanded;
  }

  // Keep the old method for backward compatibility (deprecated)
  loadDQFs() {
    this.loadCarrierAssociations();
  }
  backAction(): void {
    // Navigate to dashboard when back is clicked from Carrier Associations
    this.router.navigate(['/dashboard']);
  }

  onCarrierCardClick(card: CarrierAssociationCard): void {
    // If card has DQF, use DQF-based logic
    if (card.hasDqf && card.dqfId) {
      this.onDqfItemClick({
        id: card.dqfId,
        status: card.dqfStatus,
        carrierCompanyId: card.carrierCompanyId,
        carrierCompanyName: card.carrierCompanyName,
        carrierCompanyLogo: card.carrierCompanyLogo
      });
    } else {
      // For carriers without DQF (e.g., ELD drivers), navigate to carrier menu
      if (card.hiredStatus === HIRING_DECISION_HIRED) {
        // Navigate to carrier company menu page for hired drivers
        this.router.navigate([`${ROUTE_CARRIER_COMPANY}/${ROUTE_CARRIER_COMPANY_MENU}/${card.carrierCompanyId}/no-dqf`], {
          state: {
            companyName: card.carrierCompanyName,
            companyLogo: card.carrierCompanyLogo || '',
            dqfStatus: null,
            isEldDriver: true
          }
        });
      }
      // For non-hired associations without DQF, do nothing
    }
  }

  onDqfItemClick(dqf): void {
    let id = dqf.id;
    if (dqf.status == DQF_STATUS_CREATED) {
      if (this.driver.identityStatus == PROFILE_STATUS_INCOMPLETE) {
        let carrierName = dqf.carrierCompanyName;
        const dialogRef = this.bottomSheet.open(CompleteProfileSheetComponent, {
          panelClass: 'signature-panel',
          disableClose: true,
          data: { carrierName: carrierName }
        });
        dialogRef.afterDismissed().subscribe(res => {
          this.registrationService.navigateToProfile();
        });
      } else {
        let dqfStatus = {
          status: DQF_STATUS_PROCESSING
        };
        this.driverQualificationService.updateDqfStatus(id, dqfStatus).subscribe(res => {
          if (res && res.status === STATUS_CODE) {
            this.router.navigate([`${ROUTE_DQF}/${ROUTE_DQF_DOCUMENT_UPLOAD}/${id}`]);
          }
        });
      }
    } else {
      // Navigate to carrier company menu page
      this.router.navigate([`${ROUTE_CARRIER_COMPANY}/${ROUTE_CARRIER_COMPANY_MENU}/${dqf.carrierCompanyId}/${dqf.id}`], {
        state: {
          companyName: dqf.carrierCompanyName,
          companyLogo: dqf.carrierCompanyLogo || '',
          dqfStatus: dqf.status
        }
      });
    }
  }
  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
  isInComplete(status: string): boolean {
    if (status == DQF_STATUS_CREATED || status == DQF_STATUS_PROCESSING) {
      return true;
    } else {
      return false;
    }
  }
  isPending(status): boolean {
    return status && status == DQF_STATUS_PENDINGAPPROVAL ? true : false;
  }
  isRejected(status): boolean {
    return status && status == DQF_STATUS_REJECTED ? true : false;
  }
  isApproved(status): boolean {
    return (
      status &&
      [DQF_STATUS_APPROVED, DQF_STATUS_CARRIERPROCESSINGCOMPLETED, DQF_STATUS_APPROVEDPROCESSING].includes(status)
    );
  }
  isWithdrawn(status): boolean {
    return status && status == DQF_STATUS_WITHDRAWN;
  }

  isHired(hiredStatus: string): boolean {
    return hiredStatus === HIRING_DECISION_HIRED;
  }

  isDriverHiredByCarrier(carrierCompanyId: string): boolean {
    if (!this.driver?.companyAssociations || !Array.isArray(this.driver.companyAssociations)) {
      return false;
    }
    
    const association = this.driver.companyAssociations.find(
      assoc => assoc.id === carrierCompanyId
    );
    
    return association?.hiredStatus === 'hired';
  }
  assignCarrierCompanyLogo(uniqueCarrierId: any[]): void {
    this.isContentReady = false;
    let carrierLogoCalls = [];
    uniqueCarrierId.forEach(unique => {
      carrierLogoCalls.push(this.driverQualificationService.getCarrierCompanyLogo(unique));
    });
    forkJoin(...carrierLogoCalls)
      .pipe(
        first(),
        tap(response => {
          if (response && response.length)
            this.dqfs.forEach(dqf => {
              response.forEach((res: any) => {
                if (dqf.carrierCompanyId === res.id) {
                  if (res?.companyLogo) {
                    dqf.carrierCompanyLogo = res.companyLogo;
                  } else {
                    dqf.carrierCompanyLogo = DEAFULT_COMPANY_LOGO;
                  }
                } else {
                  dqf.carrierCompanyLogo = DEAFULT_COMPANY_LOGO;
                }
              });
            });
        }),
        catchError((error: any) => {
          this.dqfs.forEach(dqf => {
            dqf.carrierCompanyLogo = DEAFULT_COMPANY_LOGO;
          });
          return of(false);
        }),
        finalize(() => {
          this.isContentReady = true;
        })
      )
      .subscribe();
  }
  private getUniqueCarrierCompanyId(): any[] {
    let uniqueCarrierId = [];
    if (this.dqfs.length > 1) {
      for (let i = 0; i < this.dqfs.length; i++) {
        if (this.dqfs[i]?.carrierCompanyId != this.dqfs[i + i]?.carrierCompanyId) {
          uniqueCarrierId.push(this.dqfs[i]?.carrierCompanyId);
        }
      }
    } else {
      uniqueCarrierId.push(this.dqfs[0].carrierCompanyId);
    }
    return uniqueCarrierId;
  }
}
